import React from 'react';
import type { TopSearchItemWithDerived } from '@util/insights/topSearchesNov2025';

interface CompsModalProps {
  item: TopSearchItemWithDerived | null;
  isOpen: boolean;
  onClose: () => void;
}

export const CompsModal: React.FC<CompsModalProps> = ({
  item,
  isOpen,
  onClose,
}) => {
  if (!isOpen || !item) return null;

  return (
    <div
      // z-modal (400) — above the sticky header (200) and drawers (300). z-50
      // put this dialog UNDER the header, which is the whole reason the named
      // scale exists; use the token, not a magic number.
      className="fixed inset-0 z-modal flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-gray-900">
            Comparable Sales
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {/* Item Info */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {item.brand} {item.model}
            </h3>
            <p className="text-sm text-gray-600">
              Price Range: ${item.low_price.toLocaleString()} – $
              {item.high_price.toLocaleString()} (Median: $
              {item.median_price.toLocaleString()})
            </p>
          </div>

          {/* Placeholder Content */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <div className="mb-4">
              <svg
                className="w-16 h-16 mx-auto text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              Comparable Sales Data Coming Soon
            </h4>
            <p className="text-sm text-gray-600 max-w-md mx-auto">
              We're building a detailed view of recent sales, active listings,
              and market trends for this item. Check back soon for granular
              pricing insights.
            </p>
          </div>

          {/* Note for Sellers */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <span className="font-semibold">Pro tip:</span> The price range
              shown in the table is based on recent market activity. Use the
              condition adjuster to find your suggested ask based on your
              item's actual condition.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
